import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArchiveRestore, ArrowLeft, GripVertical, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    useProgramBankEntry,
    useCreateTarget,
    useUpdateTarget,
    useDeleteTarget,
    useReorderTargets,
    useRestoreTarget,
    type ChainingMethod,
    type MasteryDirection,
    type ProgramBankEntry,
    type TargetBankEntry,
    type TargetBankWritePayload,
    type TargetDataType,
} from '@/api/programBank';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';

export function ProgramDetailPage() {
    const { id = '' } = useParams<{ id: string }>();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_treatment_plans'));
    const [showArchived, setShowArchived] = useState(false);
    const { data, isLoading, isError, error } = useProgramBankEntry(id, { includeArchived: showArchived });

    const createM = useCreateTarget();
    const updateM = useUpdateTarget();
    const deleteM = useDeleteTarget();
    const reorderM = useReorderTargets();
    const restoreM = useRestoreTarget();

    const [editing, setEditing] = useState<TargetBankEntry | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmArchive, setConfirmArchive] = useState<TargetBankEntry | null>(null);

    // Active targets sort first (drag-ordered by sortOrder); archived pinned
    // to the bottom so they don't sit between draggable rows.
    const { activeTargets, archivedTargets } = useMemo(() => {
        const all = data?.targets ?? [];
        const active = all.filter((t) => !t.archived).sort((a, b) => a.sortOrder - b.sortOrder);
        const archived = all.filter((t) => t.archived).sort((a, b) => a.name.localeCompare(b.name));
        return { activeTargets: active, archivedTargets: archived };
    }, [data]);
    const targets = useMemo(
        () => [...activeTargets, ...archivedTargets],
        [activeTargets, archivedTargets],
    );

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    function handleDragEnd(e: DragEndEvent) {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIndex = activeTargets.findIndex((t) => t.id === active.id);
        const newIndex = activeTargets.findIndex((t) => t.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const reordered = activeTargets.slice();
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);
        reorderM.mutate({ programId: id, ids: reordered.map((t) => t.id) });
    }

    if (isLoading) {
        return (
            <div className="p-6 max-w-[1200px] mx-auto">
                <div className="p-12 flex items-center justify-center text-text-subtle">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                </div>
            </div>
        );
    }
    if (isError || !data) {
        return (
            <div className="p-6 max-w-[1200px] mx-auto">
                <BackLink />
                <div className="p-12 text-center text-error">
                    <p className="font-medium mb-1">Couldn't load program</p>
                    <p className="text-xs text-text-subtle">
                        {extractErrorMessage(error, 'Please try again.')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1200px] mx-auto">
            <BackLink />
            <ProgramHeader program={data} />

            <div className="flex items-center justify-between mt-6 mb-2">
                <h2 className="font-display text-lg font-bold text-text">
                    Targets
                    <span className="ml-2 text-xs font-normal text-text-subtle tabular-nums">
                        ({targets.length})
                    </span>
                </h2>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-text-muted select-none cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        Show archived
                    </label>
                    {canEdit && !data.archived && (
                        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                            <Plus className="w-3.5 h-3.5" /> Add target
                        </Button>
                    )}
                </div>
            </div>

            <Card className="p-0 overflow-hidden">
                {targets.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>No targets defined for this program yet.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    {canEdit && <th className="px-2 py-2 w-8" aria-label="Drag to reorder" />}
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold whitespace-nowrap">Data type</th>
                                    <th className="px-3 py-2 font-bold">Mastery</th>
                                    <th className="px-3 py-2 font-bold">Notes</th>
                                    {canEdit && <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>}
                                </tr>
                            </thead>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={activeTargets.map((t) => t.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <tbody>
                                        {activeTargets.map((t) => (
                                            <TargetRow
                                                key={t.id}
                                                target={t}
                                                canEdit={canEdit}
                                                draggable
                                                onEdit={() => setEditing(t)}
                                                onArchive={() => setConfirmArchive(t)}
                                                onRestore={() => restoreM.mutate({ programId: data.id, targetId: t.id })}
                                                restorePending={restoreM.isPending}
                                            />
                                        ))}
                                        {archivedTargets.map((t) => (
                                            <TargetRow
                                                key={t.id}
                                                target={t}
                                                canEdit={canEdit}
                                                draggable={false}
                                                onEdit={() => setEditing(t)}
                                                onArchive={() => setConfirmArchive(t)}
                                                onRestore={() => restoreM.mutate({ programId: data.id, targetId: t.id })}
                                                restorePending={restoreM.isPending}
                                            />
                                        ))}
                                    </tbody>
                                </SortableContext>
                            </DndContext>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <TargetDialog
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) {
                            await updateM.mutateAsync({ programId: data.id, targetId: editing.id, payload });
                        } else {
                            await createM.mutateAsync({ programId: data.id, payload });
                        }
                    }}
                />
            )}

            {confirmArchive && (
                <ArchiveTargetDialog
                    target={confirmArchive}
                    onClose={() => setConfirmArchive(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync({ programId: data.id, targetId: confirmArchive.id });
                        setConfirmArchive(null);
                    }}
                />
            )}
        </div>
    );
}

function BackLink() {
    return (
        <Link
            to="/treatment-plans?tab=programs"
            className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text mb-3"
        >
            <ArrowLeft className="w-3 h-3" /> Back to Program Bank
        </Link>
    );
}

function TargetRow({
    target,
    canEdit,
    draggable,
    onEdit,
    onArchive,
    onRestore,
    restorePending,
}: {
    target: TargetBankEntry;
    canEdit: boolean;
    draggable: boolean;
    onEdit: () => void;
    onArchive: () => void;
    onRestore: () => void;
    restorePending: boolean;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: target.id,
        disabled: !draggable,
    });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    };
    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`border-b border-border last:border-b-0 align-top transition-colors ${
                target.archived ? 'opacity-60 hover:bg-surface-muted' : 'hover:bg-surface-muted'
            }`}
        >
            {canEdit && (
                <td className="px-2 py-2 w-8 text-text-subtle">
                    {draggable ? (
                        <button
                            type="button"
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-1 -m-1 rounded hover:bg-surface-muted focus:outline-none focus:ring-1 focus:ring-accent"
                            aria-label={`Drag ${target.name}`}
                        >
                            <GripVertical className="w-3.5 h-3.5" />
                        </button>
                    ) : (
                        <span className="inline-block w-3.5 h-3.5" aria-hidden />
                    )}
                </td>
            )}
            <td className="px-3 py-2 font-medium">
                {target.name}
                {target.archived && (
                    <span className="ml-2 text-2xs uppercase tracking-wider text-text-subtle">Archived</span>
                )}
                {target.description && (
                    <div className="text-xs text-text-subtle font-normal mt-0.5 line-clamp-2">
                        {target.description}
                    </div>
                )}
            </td>
            <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                {formatDataType(target.dataType)}
            </td>
            <td className="px-3 py-2 text-text-muted">{formatMastery(target)}</td>
            <td className="px-3 py-2 text-text-muted text-xs">{formatTargetNotes(target)}</td>
            {canEdit && (
                <td className="px-3 py-2 text-right">
                    {target.archived ? (
                        <Button
                            variant="ghost"
                            size="xs"
                            aria-label={`Restore ${target.name}`}
                            disabled={restorePending}
                            onClick={onRestore}
                        >
                            <ArchiveRestore className="w-3 h-3" />
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                size="xs"
                                aria-label={`Edit ${target.name}`}
                                onClick={onEdit}
                                className="mr-1"
                            >
                                <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="xs"
                                aria-label={`Archive ${target.name}`}
                                onClick={onArchive}
                            >
                                <Trash2 className="w-3 h-3" />
                            </Button>
                        </>
                    )}
                </td>
            )}
        </tr>
    );
}

function ProgramHeader({ program }: { program: ProgramBankEntry }) {
    return (
        <div>
            <h1 className="font-display text-2xl font-bold text-text">{program.name}</h1>
            <div className="text-xs text-text-subtle mt-0.5 flex items-center gap-3">
                {program.programDomain && <span>{program.programDomain}</span>}
                {program.teachingMethod && <span>· {program.teachingMethod}</span>}
            </div>
            {program.description && (
                <p className="mt-2 text-sm text-text-muted whitespace-pre-wrap">{program.description}</p>
            )}
        </div>
    );
}

// ─── target dialog ──────────────────────────────────────────────────────────

const DATA_TYPE_OPTIONS: Array<{ value: TargetDataType; label: string }> = [
    { value: 'trial',         label: 'Trial (discrete-trial; % correct of N trials)' },
    { value: 'percent',       label: 'Percent (% correct or completed)' },
    { value: 'frequency',     label: 'Frequency (count per session or rate)' },
    { value: 'duration',      label: 'Duration (seconds)' },
    { value: 'task_analysis', label: 'Task analysis (chained steps)' },
];

const DIRECTION_OPTIONS: Array<{ value: MasteryDirection; label: string }> = [
    { value: 'at_or_above', label: 'At or above' },
    { value: 'at_or_below', label: 'At or below' },
];

const CHAINING_OPTIONS: Array<{ value: ChainingMethod; label: string }> = [
    { value: 'total_task', label: 'Total task' },
    { value: 'forward',    label: 'Forward' },
    { value: 'backward',   label: 'Backward' },
];

interface TaStep {
    label: string;
}

function toTaSteps(raw: unknown[] | null): TaStep[] {
    if (!Array.isArray(raw)) return [];
    return raw.map((s) => {
        if (s && typeof s === 'object' && 'label' in (s as Record<string, unknown>)) {
            return { label: String((s as { label: unknown }).label ?? '') };
        }
        return { label: String(s ?? '') };
    });
}

function TargetDialog({
    initial,
    onClose,
    onSubmit,
}: {
    initial: TargetBankEntry | null;
    onClose: () => void;
    onSubmit: (p: TargetBankWritePayload) => Promise<void>;
}) {
    const [name, setName]               = useState(initial?.name ?? '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [dataType, setDataType]       = useState<TargetDataType>(initial?.dataType ?? 'percent');
    const [trials, setTrials]           = useState<string>(initial?.trials != null ? String(initial.trials) : '10');
    const [taSteps, setTaSteps]         = useState<TaStep[]>(toTaSteps(initial?.taSteps ?? null));
    const [chainingMethod, setChaining] = useState<ChainingMethod>(initial?.chainingMethod ?? 'total_task');
    const [masteryDirection, setDir]    = useState<MasteryDirection>(initial?.masteryDirection ?? 'at_or_above');
    const [masteryValue, setValue]      = useState<string>(initial?.masteryValue != null ? String(initial.masteryValue) : '80');
    const [masterySessions, setMSess]   = useState<string>(initial?.masterySessions != null ? String(initial.masterySessions) : '3');
    const [generalization, setGeneralization] = useState(initial?.generalization ?? '');
    const [baselineDate, setBaselineDate]     = useState(initial?.baselineDate ?? '');
    const [baseline, setBaseline]             = useState(initial?.baseline ?? '');

    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const masteryUnit = unitForDataType(dataType);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }

        const payload: TargetBankWritePayload = {
            name: name.trim(),
            description: description.trim(),
            dataType,
            masteryDirection,
            masteryValue: masteryValue === '' ? null : Number(masteryValue),
            masterySessions: masterySessions === '' ? null : Number(masterySessions),
            generalization: generalization.trim(),
            baselineDate: baselineDate || null,
            baseline: baseline.trim(),
            trials: null,
            taSteps: null,
            chainingMethod: null,
        };

        if (dataType === 'trial') {
            if (trials === '' || Number(trials) <= 0) { setErr('Trials per session is required'); return; }
            payload.trials = Number(trials);
        } else if (dataType === 'task_analysis') {
            const cleaned = taSteps.map((s) => ({ label: s.label.trim() })).filter((s) => s.label);
            if (cleaned.length === 0) { setErr('Task analysis requires at least one step'); return; }
            payload.taSteps = cleaned;
            payload.chainingMethod = chainingMethod;
        }

        setSaving(true);
        try {
            await onSubmit(payload);
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),48rem)] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit target' : 'Add target'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-1 -mx-1">
                    <FormField label="Name" required error={err ?? undefined}>
                        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                    </FormField>
                    <FormField label="Description">
                        {(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />}
                    </FormField>
                    <FormField label="Data type" required>
                        {(id) => (
                            <select
                                id={id}
                                value={dataType}
                                onChange={(e) => setDataType(e.target.value as TargetDataType)}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                {DATA_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        )}
                    </FormField>

                    {dataType === 'trial' && (
                        <FormField label="Trials per session" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={1}
                                    value={trials}
                                    onChange={(e) => setTrials(e.target.value)}
                                />
                            )}
                        </FormField>
                    )}

                    {dataType === 'task_analysis' && (
                        <>
                            <FormField label="Chaining method" required>
                                {(id) => (
                                    <select
                                        id={id}
                                        value={chainingMethod}
                                        onChange={(e) => setChaining(e.target.value as ChainingMethod)}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                    >
                                        {CHAINING_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                )}
                            </FormField>
                            <TaStepsEditor steps={taSteps} onChange={setTaSteps} />
                        </>
                    )}

                    <div className="grid grid-cols-3 gap-x-3">
                        <FormField label="Direction">
                            {(id) => (
                                <select
                                    id={id}
                                    value={masteryDirection}
                                    onChange={(e) => setDir(e.target.value as MasteryDirection)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    {DIRECTION_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                        <FormField label={masteryUnit ? `Mastery (${masteryUnit})` : 'Mastery value'}>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    step="any"
                                    value={masteryValue}
                                    onChange={(e) => setValue(e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Consecutive sessions">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={1}
                                    value={masterySessions}
                                    onChange={(e) => setMSess(e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <FormField label="Baseline">
                        {(id) => <Textarea id={id} value={baseline} onChange={(e) => setBaseline(e.target.value)} rows={2} />}
                    </FormField>
                    <FormField label="Baseline date">
                        {(id) => <Input id={id} type="date" min="1900-01-01" max={toIsoDate(new Date())} value={baselineDate} onChange={(e) => setBaselineDate(e.target.value)} />}
                    </FormField>
                    <FormField label="Generalization">
                        {(id) => <Textarea id={id} value={generalization} onChange={(e) => setGeneralization(e.target.value)} rows={2} />}
                    </FormField>

                    <DialogFooter className="mt-2">
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function TaStepsEditor({
    steps,
    onChange,
}: {
    steps: TaStep[];
    onChange: (next: TaStep[]) => void;
}) {
    function update(i: number, label: string) {
        const next = steps.slice();
        next[i] = { label };
        onChange(next);
    }
    function add() {
        onChange([...steps, { label: '' }]);
    }
    function remove(i: number) {
        onChange(steps.filter((_, idx) => idx !== i));
    }
    function moveUp(i: number) {
        if (i === 0) return;
        const next = steps.slice();
        [next[i - 1], next[i]] = [next[i], next[i - 1]];
        onChange(next);
    }
    function moveDown(i: number) {
        if (i === steps.length - 1) return;
        const next = steps.slice();
        [next[i], next[i + 1]] = [next[i + 1], next[i]];
        onChange(next);
    }

    return (
        <div className="mb-3.5">
            <div className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">
                Task steps
                <span className="text-error ml-0.5">*</span>
            </div>
            <div className="space-y-1.5">
                {steps.length === 0 && (
                    <p className="text-xs text-text-subtle italic">No steps yet. Add at least one.</p>
                )}
                {steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-text-subtle tabular-nums w-5 text-right">{i + 1}.</span>
                        <Input
                            value={s.label}
                            onChange={(e) => update(i, e.target.value)}
                            placeholder="Step description"
                            className="h-8 py-1 flex-1"
                        />
                        <Button type="button" variant="ghost" size="xs" onClick={() => moveUp(i)} disabled={i === 0} aria-label="Move up">
                            ↑
                        </Button>
                        <Button type="button" variant="ghost" size="xs" onClick={() => moveDown(i)} disabled={i === steps.length - 1} aria-label="Move down">
                            ↓
                        </Button>
                        <Button type="button" variant="ghost" size="xs" onClick={() => remove(i)} aria-label="Remove step">
                            <Trash2 className="w-3 h-3" />
                        </Button>
                    </div>
                ))}
            </div>
            <Button type="button" variant="ghost" size="xs" onClick={add} className="mt-1.5">
                <Plus className="w-3 h-3" /> Add step
            </Button>
        </div>
    );
}

function ArchiveTargetDialog({
    target,
    onClose,
    onConfirm,
}: {
    target: TargetBankEntry;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Archive target?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Archive <span className="font-medium text-text">{target.name}</span> from this
                    program? It will be hidden from the default view, but client programs already
                    copied from this bank entry keep their own targets and aren't affected.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                setErr(extractErrorMessage(e, 'Could not archive.'));
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        {working ? 'Archiving…' : 'Archive'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── formatters (shared shape with ProgramBankPanel's detail dialog) ────────

const DATA_TYPE_LABEL: Record<TargetDataType, string> = {
    trial: 'Trial',
    percent: 'Percent',
    frequency: 'Frequency',
    duration: 'Duration',
    task_analysis: 'Task analysis',
};

function formatDataType(t: TargetDataType): string {
    return DATA_TYPE_LABEL[t] ?? t;
}

const CHAINING_LABEL: Record<ChainingMethod, string> = {
    total_task: 'total task',
    forward: 'forward chaining',
    backward: 'backward chaining',
};

function formatMastery(t: TargetBankEntry): string {
    if (t.masteryValue == null) return '—';
    const unit = unitForDataType(t.dataType);
    const comparator = t.masteryDirection === 'at_or_below' ? 'at or below' : 'at or above';
    const value = `${t.masteryValue}${unit}`;
    const sessions = t.masterySessions
        ? ` over ${t.masterySessions} consecutive session${t.masterySessions === 1 ? '' : 's'}`
        : '';
    return `${comparator} ${value}${sessions}`;
}

function unitForDataType(dataType: TargetDataType): string {
    switch (dataType) {
        case 'trial':
        case 'percent':
        case 'task_analysis':
            return '%';
        case 'duration':
            return 's';
        case 'frequency':
            return '';
        default:
            return '';
    }
}

function formatTargetNotes(t: TargetBankEntry): string {
    const parts: string[] = [];
    if (t.dataType === 'trial' && t.trials != null) {
        parts.push(`${t.trials} trials/session`);
    }
    if (t.dataType === 'task_analysis') {
        if (t.chainingMethod) parts.push(CHAINING_LABEL[t.chainingMethod] ?? t.chainingMethod);
        const stepCount = Array.isArray(t.taSteps) ? t.taSteps.length : 0;
        if (stepCount > 0) parts.push(`${stepCount} step${stepCount === 1 ? '' : 's'}`);
    }
    return parts.join(' · ') || '—';
}
